// Generator dokumen "Report Summary Clarification" per Offer (tab Clarification).
// Satu row model (buildClarificationRows) → HTML + window.print() (PDF) dan .docx.
// Value per item: final value thread yang di-lock Panitia, selain itu value
// ORIGINAL procurement/clause (tanpa overlay counter Mitra); kosong → "-".

import { displayValue } from '../../../utils/format';
import {
  flattenEvaluationDeliveryDetail,
  formatPeriodRange,
  getContractTypeLabel,
  getPaymentCurrencyLabel,
  formatProposalDeadline,
} from './tenderEvaluationHelpers';
import {
  formatOperationalTolerance,
  isValidOperationalToleranceValue,
} from '../../../shared/offer-domain/counter';
import {
  enrichDeliveryItemsWithFobLocations,
  expandFobOriginCapableRows,
  getCapableOriginNamesFromFobLocations,
  isFobOriginDisplay,
  resolveCapableLocationSelections,
} from '../../../shared/offer-domain/delivery';
import {
  getEntityDisplayName,
  getPaymentCurrencyDisplayName,
  pickLatestCounter,
  resolveDelayPenaltyAmountText,
} from '../../../shared/offer-domain/payment';
import { unwrapProcurement } from '../../../shared/offer-domain/procurement';
import {
  fetchClauseResponds,
  fetchTypeResponseResponds,
  getVisibleProcurementClauses,
  hasBidderConfirmedRow,
  mapResponsesByClauseId,
  mapResponsesByTypeResponse,
  sortRespondClauseDetails,
  toLockedFlag,
  TYPE_RESPONSE_ADDITIONAL_NOTES,
  TYPE_RESPONSE_ARBITRATION,
  TYPE_RESPONSE_DESCRIPTION_OF_PAYMENT_TERM,
  TYPE_RESPONSE_INVOICING_QUANTITY,
  TYPE_RESPONSE_LAW,
  TYPE_RESPONSE_OTHER_TERMS_AND_CONDITION,
  TYPE_RESPONSE_PAYMENT_CURRENCY,
  TYPE_RESPONSE_PAYMENT_MODE,
  TYPE_RESPONSE_PRODUCT_SPECIFICATION,
  TYPE_RESPONSE_TOLERANCE,
  TYPE_RESPONSE_VALIDITY,
  TYPE_RESPONSE_OT_VALUE,
  TYPE_RESPONSE_OT_OPTIONS,
  TYPE_RESPONSE_VALIDITY_VALID_TO,
  TYPE_RESPONSE_DESCRIPTION_OF_PAYMENT_TERM_AGREED,
  TYPE_RESPONSE_OTHER_TERMS_AND_CONDITION_AGREED,
  TYPE_RESPONSE_ADDITIONAL_NOTES_AGREED,
  TYPE_RESPONSE_COUNTER_DELAY_PENALTY_TERM,
  TYPE_RESPONSE_DELAY_PENALTY_AMOUNT,
  TYPE_RESPONSE_DESCRIPTION_OF_DELAY_PENALTY_TERM,
  TYPE_RESPONSE_DESCRIPTION_OF_DELAY_PENALTY_TERM_AGREED,
} from '../../../shared/offer-domain/respond';
import { api } from '../../../config';
import { getAuthToken } from '../../../auth/tokenvalidation';
import { withProcurementMitraId } from '../../../Components/QuantityTable/utils/quantityDeliveryUtils';
import {
  formatParcelSize,
  formatPortDateRange,
  getListData,
  mapDeliveryDetailRow,
} from './deliveryDetailUtils';

const SELLER_NAME = 'PT Pertamina Patra Niaga';
const DOCUMENT_TITLE = 'Report Tender Evaluation_Clarification';

const UI_SUBTITLE_NOISE = [
  'Click a clause to expand and edit its wording',
  'Click to view clause content',
  'Confirm the compliance requirements for this procurement',
  'Choose the payment methods and terms that apply',
  'Review procurement clauses and requirements for this tender',
  'Review procurement clauses and provide clarification responses',
  'Bisa jadi Kalimat tambahan untuk membantu section mengenai apa ini',
  'Contract and product requirements for this procurement',
];

const isValidDateForValidity = value => {
  if (value == null || value === '') return false;
  return !Number.isNaN(new Date(value).getTime());
};

const esc = s =>
  String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

const plainText = value => {
  if (value == null || typeof value === 'object') return '';
  return String(value)
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

const normalizeNoiseKey = value =>
  plainText(value)
    .toLowerCase()
    .replace(/[.\u2026]+$/g, '')
    .replace(/\s+/g, ' ')
    .trim();

const isUiSubtitleNoise = value => {
  const text = normalizeNoiseKey(value);
  if (!text) return false;
  return UI_SUBTITLE_NOISE.some(item => {
    const key = normalizeNoiseKey(item);
    return text === key || text.includes(key);
  });
};

// Placeholder "N/A" yang diketik user dianggap kosong (S-7: kosong → "-").
const isEmptyText = text => !text || /^(n\/?a|-)$/i.test(text);

const richHtml = value => {
  if (value == null || typeof value === 'object') return '-';
  if (isUiSubtitleNoise(value)) return '-';
  const text = plainText(value);
  if (isEmptyText(text)) return '-';
  const raw = String(value);
  if (/<[a-z][\s\S]*>/i.test(raw)) return raw;
  return esc(raw);
};

const resolveEntityLabel = (entity, fallback = '') =>
  displayValue(
    getEntityDisplayName(entity) || entity?.name || entity?.Name || fallback,
    ''
  );

const joinLawArbitration = (
  procurement,
  { lawLabel = null, arbitrationLabel = null } = {}
) => {
  // Label hasil resolve-by-id menang; entity hanya fallback.
  const law = displayValue(
    lawLabel || resolveEntityLabel(procurement?.law),
    ''
  );
  const arbitration = displayValue(
    arbitrationLabel || resolveEntityLabel(procurement?.arbitration),
    ''
  );
  if (law && arbitration && law !== '-' && arbitration !== '-') {
    return `${law} / ${arbitration}`;
  }
  return law && law !== '-'
    ? law
    : arbitration && arbitration !== '-'
      ? arbitration
      : '-';
};

const hasFinalText = value => !isEmptyText(plainText(value));

// Final value dari row thread terakhir yang di-lock Panitia; null bila belum.
export const pickLockedFinalValue = responses => {
  const locked = sortRespondClauseDetails(
    Array.isArray(responses) ? responses : []
  )
    .reverse()
    .find(
      item => toLockedFlag(item?.isLocked) && hasFinalText(item?.finalValue)
    );
  return locked ? locked.finalValue : null;
};

// Rich text: final (lock) menang, selain itu original.
const finalOrOriginalHtml = (finalValue, original) =>
  hasFinalText(finalValue) ? richHtml(finalValue) : richHtml(original);

// BE meng-HTML-encode label master; decode dulu supaya esc() tidak dobel.
const decodeEntities = value =>
  value
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, '&');

// Field pilihan: final berisi label master (plain).
const finalLabelHtml = finalValue =>
  hasFinalText(finalValue) ? esc(decodeEntities(plainText(finalValue))) : null;

const formatOfferValidity = value => {
  const formatted = formatProposalDeadline(value);
  if (!formatted || formatted === '-') return '-';
  return `${formatted} (Jakarta Local Time)`;
};

const formatOtOptionLabel = (procurement, optionFinal = null) => {
  const normalizeOptionLabel = value => {
    let text = String(value ?? '')
      .trim()
      .replace(/\s+'/g, "'")
      .replace(/'\s+s\b/gi, "'s");
    if (!text || text === '-') return '';
    if (/'s\s+option/i.test(text)) {
      return text.replace(/'s\s+option/i, "'s Option");
    }
    if (/option/i.test(text)) {
      return text.replace(/\s*option/i, "'s Option");
    }
    return text;
  };

  // Final OT options (type 17, lock) menang; selain itu partyType original.
  if (hasFinalText(optionFinal)) {
    return normalizeOptionLabel(decodeEntities(plainText(optionFinal)));
  }
  return normalizeOptionLabel(displayValue(procurement?.partyType, ''));
};

const buildOtSummaryLine = (
  totalQtyLabel,
  procurement,
  otFinal = null,
  optionFinal = null
) => {
  // Final OT (lock) angka → ganti tolerance; teks bebas → tampil apa adanya.
  if (hasFinalText(otFinal) && !isValidOperationalToleranceValue(otFinal)) {
    return `${totalQtyLabel} ${plainText(otFinal)}`.trim();
  }
  const tolerance = isValidOperationalToleranceValue(otFinal)
    ? otFinal
    : procurement?.operationalTolerance;
  const tolLabel = isValidOperationalToleranceValue(tolerance)
    ? `±${String(formatOperationalTolerance(tolerance)).replace(/%$/, '')}%`
    : null;
  const option = formatOtOptionLabel(procurement, optionFinal);
  if (!tolLabel && !option) return totalQtyLabel || '';
  if (tolLabel && option) {
    return `${totalQtyLabel} ${tolLabel} as operational tolerance at ${option}`;
  }
  if (tolLabel) {
    return `${totalQtyLabel} ${tolLabel} as operational tolerance`;
  }
  return `${totalQtyLabel} at ${option}`;
};

const resolveAddAldLabel = (addAld, incoterm) => {
  const raw = String(addAld ?? '')
    .trim()
    .toUpperCase();
  if (raw === 'ALD' || raw === 'ADD') return raw;
  const isFob =
    String(incoterm || '')
      .trim()
      .toUpperCase() === 'FOB';
  return isFob ? 'ALD' : 'ADD';
};

const hasPositiveQty = qty => {
  if (qty == null || qty === '') return false;
  const numeric = Number(String(qty).replace(/[^\d.-]/g, ''));
  return Number.isFinite(numeric) && numeric > 0;
};

const hasPortLabel = port => {
  const text = String(port ?? '').trim();
  return Boolean(text && text !== '-');
};

const buildLegLine = (
  qty,
  incoterm,
  route,
  port,
  addAld,
  startDate,
  endDate,
  unit
) => {
  if (!hasPositiveQty(qty) || !hasPortLabel(port)) return null;
  const qtyLabel = formatParcelSize(qty, unit);
  const place = [incoterm, route, port].filter(Boolean).join(' ').trim();
  const dateRange = formatPortDateRange(startDate, endDate);
  const addLabel = resolveAddAldLabel(addAld, incoterm);
  const datePart =
    dateRange && dateRange !== '-' ? ` on ${addLabel} ${dateRange}` : '';
  return `${qtyLabel}${place ? ` ${place}` : ''}${datePart}`.trim();
};

// Sama OfferTable: hanya location ter-select (selectedCapableLocations / listIsCapable).
const resolveFobOriginLocationNames = detail => {
  if (detail?.fobOriginLocationName) {
    return [detail.fobOriginLocationName];
  }
  const selections =
    Array.isArray(detail?.selectedCapableLocations) &&
    detail.selectedCapableLocations.length
      ? detail.selectedCapableLocations
      : resolveCapableLocationSelections(detail);
  const selectedNames = selections
    .map(loc => loc?.name ?? loc?.Name)
    .filter(Boolean);
  if (selectedNames.length) return selectedNames;
  return getCapableOriginNamesFromFobLocations(detail?.incotermFOBLocations);
};

const resolveOriginPortLabel = detail =>
  detail?.fobOriginLocationName || detail?.port1 || detail?.loadingPort || null;

const legsFromDetail = (detail, unit) => {
  if (detail?.fobOriginLocationName || detail?.isFobOriginLocationRow) {
    return [
      buildLegLine(
        detail.parcelSize ?? detail.deliveryBasis,
        detail.incoterm,
        detail.route,
        resolveOriginPortLabel(detail),
        detail.addAld,
        detail.port1StartDate,
        detail.port1EndDate,
        unit
      ),
    ].filter(Boolean);
  }

  const fobNames = resolveFobOriginLocationNames(detail);
  if (isFobOriginDisplay(detail?.route, fobNames) && fobNames.length) {
    const qty = detail.parcelSize ?? detail.deliveryBasis;
    return fobNames
      .map(locationName =>
        buildLegLine(
          qty,
          detail.incoterm,
          detail.route,
          locationName,
          detail.addAld,
          detail.port1StartDate,
          detail.port1EndDate,
          unit
        )
      )
      .filter(Boolean);
  }

  return [
    buildLegLine(
      detail.parcelSize ?? detail.deliveryBasis,
      detail.incoterm,
      detail.route,
      detail.port1 || detail.loadingPort,
      detail.addAld,
      detail.port1StartDate,
      detail.port1EndDate,
      unit
    ),
    buildLegLine(
      detail.parcelSize2,
      detail.incoterm,
      detail.route,
      detail.port2,
      detail.addAld2,
      detail.port2StartDate,
      detail.port2EndDate,
      unit
    ),
    buildLegLine(
      detail.parcelSize3,
      detail.incoterm,
      detail.route,
      detail.port3,
      detail.addAld3,
      detail.port3StartDate,
      detail.port3EndDate,
      unit
    ),
  ].filter(Boolean);
};

const sumParcelQty = details => {
  let total = 0;
  let hasNumeric = false;
  details.forEach(detail => {
    [
      detail.parcelSize ?? detail.deliveryBasis,
      detail.parcelSize2,
      detail.parcelSize3,
    ].forEach(v => {
      if (!hasPositiveQty(v)) return;
      const n = Number(String(v).replace(/[^\d.-]/g, ''));
      if (Number.isFinite(n) && n > 0) {
        total += n;
        hasNumeric = true;
      }
    });
  });
  return hasNumeric ? total : null;
};

const resolveTermDetails = (term, bidder) => {
  if (
    Array.isArray(term?._clarificationDetails) &&
    term._clarificationDetails.length
  ) {
    return term._clarificationDetails;
  }
  return getListData(term).map(raw => {
    const mapped = mapDeliveryDetailRow(bidder, raw);
    return {
      ...mapped,
      port1StartDate:
        mapped.port1StartDate ??
        raw?.startDate ??
        raw?.StartDate ??
        raw?.startDate1 ??
        raw?.StartDate1 ??
        null,
      port1EndDate:
        mapped.port1EndDate ??
        raw?.endDate ??
        raw?.EndDate ??
        raw?.endDate1 ??
        raw?.EndDate1 ??
        null,
      port2StartDate:
        mapped.port2StartDate ?? raw?.startDate2 ?? raw?.StartDate2 ?? null,
      port2EndDate:
        mapped.port2EndDate ?? raw?.endDate2 ?? raw?.EndDate2 ?? null,
      port3StartDate:
        mapped.port3StartDate ?? raw?.startDate3 ?? raw?.StartDate3 ?? null,
      port3EndDate:
        mapped.port3EndDate ?? raw?.endDate3 ?? raw?.EndDate3 ?? null,
      addAld: mapped.addAld ?? raw?.addAld ?? raw?.AddAld ?? raw?.timelineType,
      addAld2:
        mapped.addAld2 ?? raw?.addAld2 ?? raw?.AddAld2 ?? raw?.timelineType2,
      addAld3:
        mapped.addAld3 ?? raw?.addAld3 ?? raw?.AddAld3 ?? raw?.timelineType3,
    };
  });
};

const detailsForQuantityTotal = (details = []) =>
  details.filter(
    detail => !detail?.isFobOriginLocationRow || detail?.isFobOriginGroupLead
  );

const buildQuantityDeliveryHtml = (
  bidder,
  procurement,
  otFinal = null,
  optionFinal = null
) => {
  const terms = bidder?.quantityDeliveryTerms ?? [];
  const unit = procurement?.productType?.unit || 'MB';
  if (!terms.length) return '-';

  const blocks = terms.map((term, index) => {
    const cargoName =
      term?.deliveryUnit ||
      term?.DeliveryUnit ||
      `Cargo ${String.fromCharCode(65 + index)}`;
    const details = resolveTermDetails(term, bidder);
    const totalQty = sumParcelQty(detailsForQuantityTotal(details));
    const totalLabel =
      totalQty != null
        ? formatParcelSize(totalQty, unit)
        : formatParcelSize(term?.deliveryBasis ?? term?.DeliveryBasis, unit);
    const otLine = buildOtSummaryLine(
      totalLabel,
      procurement,
      otFinal,
      optionFinal
    );
    const legLines = details.flatMap(d => legsFromDetail(d, unit));
    const legsHtml = legLines.length
      ? `<div>${legLines
          .map(
            (line, i) =>
              `${esc(line)}${i < legLines.length - 1 ? esc(', and ') : ''}`
          )
          .join('')}</div>`
      : '';
    return `<div class="cargo">
      <div class="cargo-name">${esc(cargoName)}</div>
      ${otLine ? `<div>${esc(otLine)}</div>` : ''}
      ${legsHtml}
    </div>`;
  });

  return blocks.join('');
};

const metaRow = (label, valueHtml) =>
  `<tr>
    <td class="label">${esc(label)}</td>
    <td class="value">${valueHtml}</td>
  </tr>`;

const unwrapListItems = result => {
  const items =
    result?.data?.items || result?.data?.Items || result?.data || [];
  return Array.isArray(items) ? items : [];
};

const findNamedItemLabel = (items, id) => {
  if (id == null || id === '') return null;
  const match = (Array.isArray(items) ? items : []).find(
    item => String(item?.id ?? item?.Id) === String(id)
  );
  if (!match) return null;
  return (
    getEntityDisplayName(match) ||
    match?.name ||
    match?.Name ||
    match?.description ||
    match?.Description ||
    null
  );
};

const findCurrencyItemLabel = (items, id) => {
  if (id == null || id === '') return null;
  const match = (Array.isArray(items) ? items : []).find(
    item => String(item?.id ?? item?.Id) === String(id)
  );
  if (!match) return null;
  return (
    getPaymentCurrencyDisplayName(match) ||
    findNamedItemLabel([match], id) ||
    null
  );
};

const fetchJsonList = async (url, token) => {
  const response = await fetch(url, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });
  if (!response.ok) return [];
  const result = await response.json();
  return unwrapListItems(result);
};

// Resolve label master by ID (selalu), agar entity original/stale tidak menang.
export const resolveRequirementPaymentLabels = async procurement => {
  const token = getAuthToken();
  const invoicingQuantityId =
    procurement?.invoicingQuantityId ??
    procurement?.invoicingQuantity?.id ??
    null;
  const modeInvoicingQuantityId =
    procurement?.modeInvoicingQuantityId ??
    procurement?.modeInvoicingQuantity?.id ??
    null;
  const paymentCurrencyId =
    procurement?.paymentCurrencyId ?? procurement?.paymentCurrency?.id ?? null;

  let invoiceQuantityLabel =
    getEntityDisplayName(procurement?.invoicingQuantity) || null;
  let modeLabel =
    getEntityDisplayName(procurement?.modeInvoicingQuantity) || null;
  let currencyLabel =
    getPaymentCurrencyDisplayName(procurement?.paymentCurrency) ||
    getPaymentCurrencyLabel(procurement?.paymentCurrency) ||
    null;
  if (currencyLabel === '-') currencyLabel = null;

  if (!token) {
    return { invoiceQuantityLabel, modeLabel, currencyLabel };
  }

  const tasks = [];

  if (invoicingQuantityId) {
    tasks.push(
      fetchJsonList(`${api.API_URL}/invoicing-quantity`, token)
        .then(items => {
          const label = findNamedItemLabel(items, invoicingQuantityId);
          if (label) invoiceQuantityLabel = label;
        })
        .catch(() => {})
    );
  }

  if (invoicingQuantityId && modeInvoicingQuantityId) {
    tasks.push(
      fetchJsonList(
        `${api.API_URL}/invoicing-quantity/getAll-ModeInvoicingQuantity?InvoicingQuantityId=${encodeURIComponent(invoicingQuantityId)}`,
        token
      )
        .then(items => {
          const label = findNamedItemLabel(items, modeInvoicingQuantityId);
          if (label) modeLabel = label;
        })
        .catch(() => {})
    );
  }

  if (paymentCurrencyId) {
    tasks.push(
      fetchJsonList(`${api.API_URL}/paymentcurrency`, token)
        .then(items => {
          const label = findCurrencyItemLabel(items, paymentCurrencyId);
          if (label) currencyLabel = label;
        })
        .catch(() => {})
    );
  }

  if (tasks.length) await Promise.all(tasks);
  return { invoiceQuantityLabel, modeLabel, currencyLabel };
};

// Sama PaymentComplianceView: label Law/Arbitration dari master /law by ID.
export const resolveLawArbitrationLabels = async procurement => {
  const token = getAuthToken();
  const lawId =
    procurement?.lawId ?? procurement?.law?.id ?? procurement?.law?.Id ?? null;
  const arbitrationId =
    procurement?.arbitrationId ??
    procurement?.arbitration?.id ??
    procurement?.arbitration?.Id ??
    null;
  let lawLabel = getEntityDisplayName(procurement?.law) || null;
  let arbitrationLabel = getEntityDisplayName(procurement?.arbitration) || null;

  if (!token) {
    return { lawLabel, arbitrationLabel };
  }

  const tasks = [];

  if (lawId) {
    tasks.push(
      fetchJsonList(`${api.API_URL}/law`, token)
        .then(items => {
          const label = findNamedItemLabel(items, lawId);
          if (label) lawLabel = label;
        })
        .catch(() => {})
    );
  }

  if (arbitrationId) {
    tasks.push(
      fetchJsonList(`${api.API_URL}/law/getAll-arbitration`, token)
        .then(items => {
          const label = findNamedItemLabel(items, arbitrationId);
          if (label) arbitrationLabel = label;
        })
        .catch(() => {})
    );
  }

  if (tasks.length) await Promise.all(tasks);
  return { lawLabel, arbitrationLabel };
};

const buildPaymentModeHtml = (procurement, modeLabel) => {
  // Label resolve-by-id menang; entity hanya fallback.
  const mode = displayValue(
    modeLabel || getEntityDisplayName(procurement?.modeInvoicingQuantity),
    '-'
  );
  return mode && mode !== '-' ? esc(mode) : '-';
};

const resolvePaymentCurrencyHtml = (procurement, currencyLabel = null) => {
  if (currencyLabel && currencyLabel !== '-') return esc(currencyLabel);
  const fromHelper = getPaymentCurrencyLabel(procurement?.paymentCurrency);
  if (fromHelper && fromHelper !== '-') return esc(fromHelper);
  const fromDisplay = getPaymentCurrencyDisplayName(
    procurement?.paymentCurrency
  );
  return fromDisplay ? esc(fromDisplay) : '-';
};

const formatValidityFinal = finalValue =>
  isValidDateForValidity(finalValue)
    ? esc(formatOfferValidity(finalValue))
    : esc(plainText(finalValue));

/**
 * Row model Report Summary Clarification, dipakai renderer HTML (PDF) & DOCX.
 * `clauseRespondsById` = respond clause per procurementClauseDetailId,
 * `typeRespondsByType` = respond type per TypeResponse; keduanya membawa
 * `finalValue` + `isLocked`. Return { documentTitle, intro, rows: [{label, html}] }.
 */
export const buildClarificationRows = ({
  procurement,
  bidder,
  modeLabel = null,
  invoiceQuantityLabel = null,
  currencyLabel = null,
  lawLabel = null,
  arbitrationLabel = null,
  clauseRespondsById = {},
  typeRespondsByType = {},
} = {}) => {
  const source = procurement || {};
  const bidderMitraId = resolveMitraId(bidder);
  const finalOf = type =>
    pickLockedFinalValue(
      typeRespondsByType?.[type] ?? typeRespondsByType?.[String(type)]
    );
  const productName = displayValue(source?.productType?.product?.name, '-');
  const period = formatPeriodRange(source);
  const contractType = getContractTypeLabel(source?.contractType);
  const mitraName = displayValue(bidder?.companyName, '-');
  const intro = `Based on our evaluation and clarification results for the ${productName} supply purchase ${period} Delivery (${contractType}), we are pleased to share the summary details as follows:`;

  // Clause: satu row per clause, final (lock) atau deskripsi original.
  const clauses = getVisibleProcurementClauses(source);
  const clauseRows = clauses.length
    ? clauses.map(clause => {
        const clauseId = clause?.id ?? clause?.procurementClauseDetailId;
        const finalValue = pickLockedFinalValue(
          clauseRespondsById?.[clauseId] ||
            clauseRespondsById?.[String(clauseId)]
        );
        return {
          label: plainText(clause.clauseTitle) || 'Clause Detail',
          html: finalOrOriginalHtml(finalValue, clause.clauseDescription),
        };
      })
    : [{ label: 'Clause Detail', html: '-' }];

  const invoiceHtml =
    finalLabelHtml(finalOf(TYPE_RESPONSE_INVOICING_QUANTITY)) ||
    (() => {
      const label = displayValue(
        invoiceQuantityLabel || getEntityDisplayName(source?.invoicingQuantity),
        ''
      );
      return label && label !== '-' ? esc(label) : null;
    })();
  // Free-text: agree per field (19/20/21, HTML) menang; lock thread lama
  // (11/14/15) = fallback.
  const freeTextFinalOf = (agreedType, threadType) =>
    finalOf(agreedType) ?? finalOf(threadType);
  const descriptionHtml = finalOrOriginalHtml(
    freeTextFinalOf(
      TYPE_RESPONSE_DESCRIPTION_OF_PAYMENT_TERM_AGREED,
      TYPE_RESPONSE_DESCRIPTION_OF_PAYMENT_TERM
    ),
    source?.descriptionOfPaymentTerms
  );
  const delayPenaltyDescriptionHtml = finalOrOriginalHtml(
    freeTextFinalOf(
      TYPE_RESPONSE_DESCRIPTION_OF_DELAY_PENALTY_TERM_AGREED,
      TYPE_RESPONSE_DESCRIPTION_OF_DELAY_PENALTY_TERM
    ),
    source?.descriptionOfDelayPenaltyTerms
  );
  // Agree per field (16/17/18) menang; thread lock lama (1/2) = fallback.
  const validityFinal =
    finalOf(TYPE_RESPONSE_VALIDITY_VALID_TO) ?? finalOf(TYPE_RESPONSE_VALIDITY);

  const rows = [
    { label: 'Seller', html: esc(SELLER_NAME) },
    { label: 'Buyer', html: esc(mitraName) },
    { label: 'Product', html: esc(productName) },
    { label: 'Contract Period', html: esc(period) },
    {
      label: 'Product Specification',
      html: finalOrOriginalHtml(
        finalOf(TYPE_RESPONSE_PRODUCT_SPECIFICATION),
        source?.productSpecification
      ),
    },
    {
      label: 'Quantity & Mode of Delivery',
      html: buildQuantityDeliveryHtml(
        bidder,
        source,
        finalOf(TYPE_RESPONSE_OT_VALUE) ?? finalOf(TYPE_RESPONSE_TOLERANCE),
        finalOf(TYPE_RESPONSE_OT_OPTIONS)
      ),
    },
    ...clauseRows,
    {
      label: 'Delay Penalty Amount',
      // Final agreed (23) > counter Mitra > confirm (22) > amount legacy.
      html:
        finalLabelHtml(finalOf(TYPE_RESPONSE_DELAY_PENALTY_AMOUNT)) ||
        esc(
          resolveDelayPenaltyAmountText({
            counterAmount: bidderMitraId
              ? pickLatestCounter(
                  source?.counterDelayPenaltyTerm,
                  bidderMitraId
                )?.delayPenaltyAmount
              : null,
            isConfirmed: hasBidderConfirmedRow(
              typeRespondsByType?.[TYPE_RESPONSE_COUNTER_DELAY_PENALTY_TERM]
            ),
            buyerAmount: source?.delayPenaltyAmount,
          })
        ),
    },
    delayPenaltyDescriptionHtml !== '-'
      ? {
          label: 'Description of Delay Penalty Terms',
          html: delayPenaltyDescriptionHtml,
        }
      : null,
    invoiceHtml ? { label: 'Invoicing Quantity', html: invoiceHtml } : null,
    {
      label: 'Payment Mode',
      html:
        finalLabelHtml(finalOf(TYPE_RESPONSE_PAYMENT_MODE)) ||
        buildPaymentModeHtml(source, modeLabel),
    },
    {
      label: 'Payment Currency',
      html:
        finalLabelHtml(finalOf(TYPE_RESPONSE_PAYMENT_CURRENCY)) ||
        resolvePaymentCurrencyHtml(source, currencyLabel),
    },
    descriptionHtml !== '-'
      ? { label: 'Description of Payment Term', html: descriptionHtml }
      : null,
    {
      label: 'Law & Arbitration',
      html: esc(
        joinLawArbitration(source, {
          lawLabel: plainText(finalOf(TYPE_RESPONSE_LAW)) || lawLabel,
          arbitrationLabel:
            plainText(finalOf(TYPE_RESPONSE_ARBITRATION)) || arbitrationLabel,
        })
      ),
    },
    {
      label: 'Other Terms & Condition',
      html: finalOrOriginalHtml(
        freeTextFinalOf(
          TYPE_RESPONSE_OTHER_TERMS_AND_CONDITION_AGREED,
          TYPE_RESPONSE_OTHER_TERMS_AND_CONDITION
        ),
        source?.otherTermsAndCondition
      ),
    },
    {
      label: 'Offer Validity',
      html: hasFinalText(validityFinal)
        ? formatValidityFinal(validityFinal)
        : esc(formatOfferValidity(source?.proposalValidityPeriodMax)),
    },
    {
      label: 'Additional Notes',
      html: finalOrOriginalHtml(
        freeTextFinalOf(
          TYPE_RESPONSE_ADDITIONAL_NOTES_AGREED,
          TYPE_RESPONSE_ADDITIONAL_NOTES
        ),
        source?.additionalNotes
      ),
    },
  ].filter(Boolean);

  return {
    documentTitle: `${DOCUMENT_TITLE} ${mitraName}`,
    intro,
    rows,
  };
};

const renderClarificationHtml = ({
  documentTitle,
  intro,
  rows,
}) => `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8" />
<title>${esc(documentTitle)}</title>
<style>
  body { font-family: Calibri, Arial, Helvetica, sans-serif; font-size: 12px; color: #222; margin: 0; background: #9a9a9a; }
  .sheet { width: 210mm; min-height: 297mm; margin: 24px auto; padding: 15mm 18mm; background: #fff; box-shadow: 0 2px 12px rgba(0,0,0,.45); box-sizing: border-box; }
  h1 { font-size: 16px; text-align: center; margin: 0 0 14px; font-weight: bold; text-transform: uppercase; }
  .intro { margin: 0 0 16px; line-height: 1.45; }
  table.meta { border-collapse: collapse; width: 100%; }
  table.meta td { border: 0.35mm solid #555; padding: 6px 8px; vertical-align: top; }
  table.meta td.label { width: 32%; font-weight: bold; background: #f7f7f7; }
  table.meta td.value { width: 68%; }
  .cargo { margin: 0 0 6px; line-height: 1.35; }
  .cargo-name { font-weight: bold; }
  .cargo div { margin: 0; }
  @media print {
    @page { size: A4; margin: 15mm; }
    body { margin: 0; background: #fff; }
    .sheet { width: auto; min-height: 0; margin: 0; padding: 0; background: #fff; box-shadow: none; }
  }
</style>
</head>
<body>
<div class="sheet">
  <h1>Report Summary Clarification</h1>
  <p class="intro">${esc(intro)}</p>
  <table class="meta">
    ${rows.map(row => metaRow(row.label, row.html)).join('')}
  </table>
</div>
</body>
</html>`;

export const buildClarificationHtml = params =>
  renderClarificationHtml(buildClarificationRows(params));

/* ------------------------------ Word (.docx) ----------------------------- */

const DOC_BLOCK_TAGS = new Set([
  'P',
  'DIV',
  'LI',
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
  'TR',
  'BLOCKQUOTE',
  'PRE',
]);

/**
 * HTML cell (rich editor / cargo) → paragraf sederhana untuk docx:
 * [{ level, runs: [{ text, bold, italics, underline }] }]. List jadi
 * prefix "• " / "1. " + indent per level. Exported untuk test.
 */
export const htmlToDocBlocks = html => {
  const doc = new DOMParser().parseFromString(
    `<body>${html ?? ''}</body>`,
    'text/html'
  );
  const blocks = [];
  let current = null;

  const flush = () => {
    if (current?.runs.some(run => run.text.trim())) {
      const { runs } = current;
      runs[0].text = runs[0].text.replace(/^\s+/, '');
      runs[runs.length - 1].text = runs[runs.length - 1].text.replace(
        /\s+$/,
        ''
      );
      blocks.push(current);
    }
    current = null;
  };

  const walk = (node, fmt, level) => {
    if (node.nodeType === 3) {
      const text = node.textContent.replace(/\s+/g, ' ');
      if (!text) return;
      if (!current) current = { level, runs: [] };
      current.runs.push({ text, ...fmt });
      return;
    }
    if (node.nodeType !== 1) return;
    const tag = node.tagName;
    if (tag === 'BR') {
      flush();
      return;
    }
    const next = {
      bold:
        fmt.bold ||
        tag === 'B' ||
        tag === 'STRONG' ||
        /^H\d$/.test(tag) ||
        node.classList.contains('cargo-name'),
      italics: fmt.italics || tag === 'I' || tag === 'EM',
      underline: fmt.underline || tag === 'U',
    };
    if (tag === 'UL' || tag === 'OL') {
      flush();
      let index = 0;
      node.childNodes.forEach(child => {
        if (child.tagName !== 'LI') {
          walk(child, next, level + 1);
          return;
        }
        index += 1;
        flush();
        current = {
          level: level + 1,
          runs: [{ text: tag === 'OL' ? `${index}. ` : '• ', ...next }],
        };
        child.childNodes.forEach(grand => walk(grand, next, level + 1));
        flush();
      });
      return;
    }
    const isBlock = DOC_BLOCK_TAGS.has(tag);
    if (isBlock) flush();
    node.childNodes.forEach(child => walk(child, next, level));
    if (isBlock) flush();
  };

  walk(doc.body, { bold: false, italics: false, underline: false }, 0);
  flush();
  return blocks;
};

const safeFileName = value =>
  String(value || '')
    .replace(/[\\/:*?"<>|]+/g, '_')
    .trim();

const triggerDownload = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const renderClarificationDocxBlob = async ({ intro, rows }) => {
  const {
    AlignmentType,
    BorderStyle,
    Document,
    Packer,
    Paragraph,
    ShadingType,
    Table,
    TableCell,
    TableRow,
    TextRun,
    WidthType,
  } = await import('docx');

  const FONT = 'Calibri';
  const SIZE = 20; // half-point → 10pt
  const pct = size => ({ size, type: WidthType.PERCENTAGE });
  const line = { style: BorderStyle.SINGLE, size: 6, color: '555555' };
  const borders = { top: line, bottom: line, left: line, right: line };
  const margins = { top: 80, bottom: 80, left: 120, right: 120 };

  const valueParagraphs = html => {
    const blocks = htmlToDocBlocks(html);
    if (!blocks.length) {
      return [
        new Paragraph({
          children: [new TextRun({ text: '-', font: FONT, size: SIZE })],
        }),
      ];
    }
    return blocks.map(
      block =>
        new Paragraph({
          indent: block.level ? { left: 360 * block.level } : undefined,
          spacing: { after: 60 },
          children: block.runs.map(
            run =>
              new TextRun({
                text: run.text,
                bold: run.bold || undefined,
                italics: run.italics || undefined,
                underline: run.underline ? {} : undefined,
                font: FONT,
                size: SIZE,
              })
          ),
        })
    );
  };

  const table = new Table({
    width: pct(100),
    rows: rows.map(
      row =>
        new TableRow({
          children: [
            new TableCell({
              width: pct(32),
              borders,
              margins,
              shading: {
                fill: 'F7F7F7',
                type: ShadingType.CLEAR,
                color: 'auto',
              },
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: row.label,
                      bold: true,
                      font: FONT,
                      size: SIZE,
                    }),
                  ],
                }),
              ],
            }),
            new TableCell({
              width: pct(68),
              borders,
              margins,
              children: valueParagraphs(row.html),
            }),
          ],
        })
    ),
  });

  const doc = new Document({
    sections: [
      {
        properties: {
          page: {
            size: { width: 11906, height: 16838 }, // A4
            margin: { top: 850, bottom: 850, left: 850, right: 850 }, // 15mm
          },
        },
        children: [
          new Paragraph({
            alignment: AlignmentType.CENTER,
            spacing: { after: 240 },
            children: [
              new TextRun({
                text: 'REPORT SUMMARY CLARIFICATION',
                bold: true,
                font: FONT,
                size: 28,
              }),
            ],
          }),
          new Paragraph({
            alignment: AlignmentType.JUSTIFIED,
            spacing: { after: 240 },
            children: [new TextRun({ text: intro, font: FONT, size: SIZE })],
          }),
          table,
        ],
      },
    ],
  });
  return Packer.toBlob(doc);
};

const openDocWindow = html => {
  const w = window.open('', '_blank');
  if (!w) return null;
  w.document.write(html);
  w.document.close();
  return w;
};

const pickFirstDateValue = (...values) => {
  for (const value of values) {
    if (value == null || value === '') continue;
    const text = String(value).trim();
    if (!text || text === '-') continue;
    return value;
  }
  return null;
};

const mergeDetailRowDates = (item, match) => {
  if (!match) return item;
  return {
    ...item,
    port1StartDate: pickFirstDateValue(
      item.port1StartDate,
      match.port1StartDate,
      match.startDate
    ),
    port1EndDate: pickFirstDateValue(
      item.port1EndDate,
      match.port1EndDate,
      match.endDate
    ),
    port2StartDate: pickFirstDateValue(
      item.port2StartDate,
      match.port2StartDate,
      match.startDate2
    ),
    port2EndDate: pickFirstDateValue(
      item.port2EndDate,
      match.port2EndDate,
      match.endDate2
    ),
    port3StartDate: pickFirstDateValue(
      item.port3StartDate,
      match.port3StartDate,
      match.startDate3
    ),
    port3EndDate: pickFirstDateValue(
      item.port3EndDate,
      match.port3EndDate,
      match.endDate3
    ),
    addAld: pickFirstDateValue(item.addAld, match.addAld),
    addAld2: pickFirstDateValue(item.addAld2, match.addAld2),
    addAld3: pickFirstDateValue(item.addAld3, match.addAld3),
    port1: item.port1 || match.port1 || item.loadingPort || match.loadingPort,
    port2: item.port2 || match.port2,
    port3: item.port3 || match.port3,
    route: item.route || match.route,
    incotermFOBLocations:
      item.incotermFOBLocations?.length > 0
        ? item.incotermFOBLocations
        : match.incotermFOBLocations,
    fobLocationNames:
      item.fobLocationNames?.length > 0
        ? item.fobLocationNames
        : match.fobLocationNames,
  };
};

const clarificationRowKey = row =>
  `${row?.quantityInformationDetailId ?? ''}|${row?.quantityIncotermId ?? ''}`;

/**
 * be#456: baris bidder (get-submitted-bidder) = acuan; baris get-delivery-detail
 * bidder itu di-merge per key QID|incoterm (fallback QID saja). Baris bidder
 * tanpa pasangan tetap dipertahankan; baris detail tanpa pasangan diabaikan.
 */
export const mergeClarificationDetailRows = (
  listMapped = [],
  detailRows = [],
  { termId = null, bidderId = null } = {}
) => {
  if (!detailRows.length) return listMapped;

  const byKey = new Map();
  detailRows.forEach(row => {
    if (!row?.quantityInformationDetailId) return;
    byKey.set(clarificationRowKey(row), row);
    const looseKey = `${row.quantityInformationDetailId}|`;
    if (!byKey.has(looseKey)) byKey.set(looseKey, row);
  });

  return listMapped.map(listHit => {
    const row =
      byKey.get(clarificationRowKey(listHit)) ??
      byKey.get(`${listHit?.quantityInformationDetailId ?? ''}|`);
    if (!row) return listHit;

    const merged = mergeDetailRowDates(
      {
        ...row,
        quantityDeliveryTermId: termId,
        procurementMitraId: bidderId,
        parentId: bidderId,
        quantityIncotermId:
          row.quantityIncotermId || listHit.quantityIncotermId || null,
      },
      listHit
    );
    return {
      ...merged,
      incotermFOBLocations:
        listHit.incotermFOBLocations?.length > 0
          ? listHit.incotermFOBLocations
          : merged.incotermFOBLocations,
      fobLocationNames:
        listHit.fobLocationNames?.length > 0
          ? listHit.fobLocationNames
          : merged.fobLocationNames,
      selectedCapableLocations:
        listHit.selectedCapableLocations?.length > 0
          ? listHit.selectedCapableLocations
          : merged.selectedCapableLocations,
      listIsCapable:
        listHit.listIsCapable?.length > 0
          ? listHit.listIsCapable
          : merged.listIsCapable,
    };
  });
};

const enrichBidderDeliveryDates = async (procurement, bidder) => {
  const procurementId = procurement?.id;
  const token = getAuthToken();
  const terms =
    bidder?.quantityDeliveryTerms ?? bidder?.QuantityDeliveryTerms ?? [];
  if (!token || !terms.length) return bidder;

  const bidderMap = { [String(bidder.id)]: bidder };
  const nextTerms = [];

  for (const term of terms) {
    const termId = term?.id ?? term?.Id;
    const listMapped = getListData(term).map((raw, requirementIndex) => ({
      ...mapDeliveryDetailRow(bidder, raw),
      quantityDeliveryTermId: termId,
      quantityIncotermId:
        raw?.quantityIncotermId ?? raw?.QuantityIncotermId ?? null,
      procurementMitraId: bidder.id,
      parentId: bidder.id,
      requirementIndex,
      deliveryUnit: term?.deliveryUnit ?? term?.DeliveryUnit,
    }));

    let items = listMapped;

    if (termId && procurementId) {
      try {
        // be#456: nilai offer milik bidder dokumen ini (ProcurementMitraId).
        const response = await fetch(
          withProcurementMitraId(
            `${api.API_URL}/procurement/get-delivery-detail?PageNumber=1&PageSize=500&procurementId=${encodeURIComponent(procurementId)}&quantityDeliveryTermId=${encodeURIComponent(termId)}`,
            bidder.id
          ),
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            credentials: 'same-origin',
          }
        );
        if (response.ok) {
          const result = await response.json();
          const detailRows = flattenEvaluationDeliveryDetail(
            result,
            bidderMap,
            termId,
            bidder.id
          ).filter(
            row =>
              String(row?.procurementMitraId ?? row?.parentId ?? '') ===
              String(bidder.id)
          );
          items = mergeClarificationDetailRows(listMapped, detailRows, {
            termId,
            bidderId: bidder.id,
          });
        }
      } catch {
        // keep listMapped when delivery-detail fetch fails
      }
    }

    if (termId) {
      try {
        items = await enrichDeliveryItemsWithFobLocations({
          quantityDeliveryTermId: termId,
          token,
          items,
        });
      } catch {
        // keep items when FOB/timeline enrich fails
      }
    }

    // Sama OfferTable: satu row per selected capable Origin location.
    items = expandFobOriginCapableRows(items);

    nextTerms.push({
      ...term,
      _clarificationDetails: items,
    });
  }

  return {
    ...bidder,
    quantityDeliveryTerms: nextTerms,
    QuantityDeliveryTerms: nextTerms,
  };
};

const resolveMitraId = bidder =>
  bidder?.id ??
  bidder?.procurementMitraId ??
  bidder?.ProcurementMitraId ??
  null;

const fetchFreshProcurement = async (procurementId, token) => {
  if (!procurementId || !token) return null;
  try {
    const response = await fetch(
      `${api.API_URL}/procurement/${encodeURIComponent(procurementId)}`,
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }
    );
    if (!response.ok) return null;
    const result = await response.json();
    return unwrapProcurement(result);
  } catch {
    return null;
  }
};

const loadClarificationReport = async ({ procurement, bidder }) => {
  const token = getAuthToken();
  const mitraId = resolveMitraId(bidder);
  const procurementId = procurement?.id ?? bidder?.procurementId;
  const freshProcurement = await fetchFreshProcurement(procurementId, token);
  // Value original procurement; counter Mitra tidak di-overlay.
  const procurementForDoc = freshProcurement || procurement;
  const respondScope = {
    token,
    procurementId: procurementId ?? procurementForDoc?.id,
    procurementMitraId: mitraId,
  };

  const [
    { modeLabel, invoiceQuantityLabel, currencyLabel },
    { lawLabel, arbitrationLabel },
    enrichedBidder,
    clauseRespondItems,
    typeRespondItems,
  ] = await Promise.all([
    resolveRequirementPaymentLabels(procurementForDoc),
    resolveLawArbitrationLabels(procurementForDoc),
    enrichBidderDeliveryDates(procurementForDoc, bidder),
    fetchClauseResponds(respondScope),
    // Tanpa mitraId list type tidak ter-scope → jangan ambil final Mitra lain.
    mitraId ? fetchTypeResponseResponds(respondScope) : [],
  ]);

  return buildClarificationRows({
    procurement: procurementForDoc,
    bidder: enrichedBidder,
    modeLabel,
    invoiceQuantityLabel,
    currencyLabel,
    lawLabel,
    arbitrationLabel,
    clauseRespondsById: mapResponsesByClauseId(clauseRespondItems),
    typeRespondsByType: mapResponsesByTypeResponse(typeRespondItems),
  });
};

export const printClarificationPdf = async ({ procurement, bidder }) => {
  const model = await loadClarificationReport({ procurement, bidder });
  const w = openDocWindow(renderClarificationHtml(model));
  if (!w) return;
  w.focus();
  setTimeout(() => w.print(), 400);
};

export const downloadClarificationDocx = async ({ procurement, bidder }) => {
  const model = await loadClarificationReport({ procurement, bidder });
  const blob = await renderClarificationDocxBlob(model);
  triggerDownload(blob, `${safeFileName(model.documentTitle)}.docx`);
};
